import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SiteDataContext = createContext({
  categories: [],
  siteSettings: null,
  loading: true,
  refresh: () => {},
});

export const SiteDataProvider = ({ children }) => {
  const [categories, setCategories] = useState([]);
  const [siteSettings, setSiteSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const [catRes, setRes] = await Promise.all([
        axios.get(`${API}/categories?active_only=true`),
        axios.get(`${API}/site-settings`),
      ]);
      setCategories(catRes.data || []);
      setSiteSettings(setRes.data || null);
    } catch (err) {
      console.error('SiteData load failed', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return (
    <SiteDataContext.Provider value={{ categories, siteSettings, loading, refresh }}>
      {children}
    </SiteDataContext.Provider>
  );
};

export const useSiteData = () => useContext(SiteDataContext);

/**
 * Find category for a property, tolerating case + plural mismatch (Plot vs Plots).
 */
export const findCategoryForProperty = (property, categories) => {
  if (!property || !categories?.length) return null;
  const value = String(property.category || '').toLowerCase().trim();
  return (
    categories.find((c) => c.name_en?.toLowerCase() === value) ||
    categories.find((c) => c.slug?.toLowerCase() === value) ||
    categories.find((c) => {
      const n = c.name_en?.toLowerCase() || '';
      return n === value || n === `${value}s` || n === value.replace(/s$/, '') || c.slug?.toLowerCase() === value.replace(/s$/, '');
    }) || null
  );
};

/**
 * Resolve effective field visibility for a property.
 * Order: property.field_visibility > category.default_visibility > {all true}
 */
export const resolveVisibility = (property, categories) => {
  const all = {
    show_total_price: true,
    show_rate_per_sqft: true,
    show_gov_rate: true,
    show_area: true,
    show_price_range: true,
  };
  const cat = findCategoryForProperty(property, categories);
  return {
    ...all,
    ...(cat?.default_visibility || {}),
    ...(property?.field_visibility || {}),
  };
};
